import Head from 'expo-router/head';
import BedDouble from 'lucide-react-native/icons/bed-double';
import MapIcon from 'lucide-react-native/icons/map';
import MapPin from 'lucide-react-native/icons/map-pin';
import { useEffect, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { HotelsMapPanel } from '@/components/detail/hotels-map';
import { Faq, landingStyles, MoreLink, SectionHeading } from '@/components/riviera/landing-parts';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { Button } from '@/components/ui/button';
import { Fonts, Radius, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import { cities } from '@/lib/events-data';
import {
  cityStay,
  RIVIERA_STAY,
  RIVIERA_ZOOM,
  RIVIERA_ZOOM_NARROW,
  STAY22_SOURCE,
  stay22MapUrl,
  stay22Url,
} from '@/lib/hotels';
import { hotelsPageCopy } from '@/lib/i18n/hotels-page-content';
import type { Locale } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';
import { openExternal } from '@todo/core/open-external';
import { absoluteUrl, breadcrumbJsonLd, faqPageJsonLd, hreflangLinks, seoTitle } from '@todo/core/seo';
import { SITE } from '@/site';

// "Where to stay on the French Riviera" — the header's "Hotels" nav item.
// Stay22's map of hotels and rentals for the whole coast, with a button per
// town that re-centres it, then hand-written notes on which part of each town
// to stay in (`hotelsPage` in the i18n resources) and a FAQ — the indexable
// part, since the map is an iframe that crawlers read nothing from.
//
// Unlike every other Stay22 map on the site (click-to-load, hotels-map.web.tsx),
// this one loads with the page: the map is the page's point (owner's call —
// see AGENTS.md). Don't copy that to other pages.
//
// Every "See hotels" button is an Allez link built on press and tracked as an
// `affiliate-click` (kind 'hotel', id 'hotels'); clicks inside the map iframe
// reach only Stay22's reports, under the `hotels_<area>_map` campaign.

/** Umami `id` of this page's hotel clicks. */
const PAGE_ID = 'hotels';

/** SITE.stay22.region ('riviera', the whole coast) or a city id. */
type Area = string;
const RIVIERA: Area = SITE.stay22.region;

// One town on the map, as on the city guides' own map.
const CITY_ZOOM = 13;

// use-breakpoint.ts's `sm`: below it the whole coast needs one zoom level less.
const NARROW_QUERY = '(max-width: 639px)';

/** Web only, and only after mount — the static HTML is the desktop map. */
function isNarrow(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(NARROW_QUERY).matches
    : false;
}

function mapSrc(area: Area, locale: Locale, narrow = false): string {
  const stay = cityStay(area);
  if (!stay) {
    return stay22MapUrl(RIVIERA_STAY, {
      campaign: `hotels_${RIVIERA}`,
      locale,
      zoom: narrow ? RIVIERA_ZOOM_NARROW : RIVIERA_ZOOM,
      hideMainMarker: true,
    });
  }
  // A town's centroid marks nothing a visitor would look for either.
  return stay22MapUrl(stay, { campaign: `hotels_${area}`, locale, zoom: CITY_ZOOM, hideMainMarker: true });
}

function openCityHotels(cityId: string, locale: Locale) {
  const stay = cityStay(cityId);
  if (!stay) return;
  openExternal(stay22Url(stay, { campaign: `hotels_${cityId}`, locale }), {
    kind: 'hotel',
    source: STAY22_SOURCE,
    id: PAGE_ID,
    city: cityId,
    locale,
  });
}

/** A town button over the map: a toggle, not a link — it only moves the map. */
function AreaChip({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  const theme = useTheme();
  return (
    <Pressable accessibilityRole="button" accessibilityState={{ selected: active }} onPress={onPress}>
      {({ pressed }) => (
        <View
          style={[
            styles.chip,
            {
              backgroundColor: active ? theme.accent : theme.card,
              borderColor: active ? theme.accent : theme.border,
            },
            pressed && { opacity: 0.85 },
          ]}>
          <MapPin size={12} color={active ? theme.accentForeground : theme.mutedForeground} />
          <Text style={[styles.chipText, { color: active ? theme.accentForeground : theme.text }]}>
            {label}
          </Text>
        </View>
      )}
    </Pressable>
  );
}

export default function HotelsScreen() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const copy = hotelsPageCopy(locale);
  const canonical = absoluteUrl(locale, 'hotels');
  const title = seoTitle(t('hotelsPage.seoTitle'));
  const description = t('hotelsPage.metaDescription');

  const [area, setArea] = useState<Area>(RIVIERA);
  // The widget's URL, starting on the whole coast. It carries no dates, so
  // it's the same at export time and in the browser: the iframe is baked into
  // the static HTML and loads with the page.
  const [src, setSrc] = useState(() => mapSrc(RIVIERA, locale));
  const [focusKey, setFocusKey] = useState(0);

  // A phone swaps the baked desktop map for the zoomed-out one right after
  // hydration, so the whole coast fits its width too.
  useEffect(() => {
    if (isNarrow()) setSrc(mapSrc(RIVIERA, locale, true));
  }, [locale]);

  // Town buttons and "Show on the map" move the map and scroll to it.
  const showOnMap = (next: Area) => {
    setArea(next);
    setSrc(mapSrc(next, locale, isNarrow()));
    setFocusKey((k) => k + 1);
  };

  const inArea = area === RIVIERA ? null : t(`boatRental.cities.${area}.inCity`);

  return (
    <View style={[landingStyles.root, { backgroundColor: theme.background }]}>
      {/* key forces a fresh mount on locale change — see the comment in
          app/[lang]/_layout.tsx for why. */}
      <Head key={locale}>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        {hreflangLinks('hotels').map((link) => (
          <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
        ))}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd(locale, [{ name: t('nav.hotels') }]))}
        </script>
        <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(copy.faq))}</script>
      </Head>
      <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
        <SiteHeader />
      </SafeAreaView>
      <ScrollView style={{ flex: 1 }}>
        <View style={landingStyles.centered}>
          <View style={landingStyles.page}>
            <Text
              role="heading"
              aria-level={1}
              style={[landingStyles.heading, { color: theme.text, fontFamily: Fonts?.serif }]}>
              {t('hotelsPage.heading')}
            </Text>
            {copy.intro.map((paragraph) => (
              <Text key={paragraph} style={[landingStyles.intro, { color: theme.textSecondary }]}>
                {paragraph}
              </Text>
            ))}

            <View style={landingStyles.chipsRow}>
              <AreaChip
                label={t('hotelsPage.wholeRiviera')}
                active={area === RIVIERA}
                onPress={() => showOnMap(RIVIERA)}
              />
              {cities.map((city) => (
                <AreaChip
                  key={city.id}
                  label={copy.cities[city.id]?.name ?? city.name}
                  active={area === city.id}
                  onPress={() => showOnMap(city.id)}
                />
              ))}
            </View>
            <HotelsMapPanel src={src} focusKey={focusKey} />
            {inArea ? (
              <Button
                variant="outline"
                style={styles.areaCta}
                leadingIcon={<BedDouble size={18} color={theme.text} />}
                onPress={() => openCityHotels(area, locale)}>
                {t('hotels.ctaCity', { inCity: inArea })}
              </Button>
            ) : null}

            <View style={landingStyles.section}>
              <SectionHeading>{t('hotelsPage.townsHeading')}</SectionHeading>
              {cities.map((city) => {
                const inCity = t(`boatRental.cities.${city.id}.inCity`);
                return (
                  <View
                    key={city.id}
                    style={[styles.town, { backgroundColor: theme.card, borderColor: theme.border }]}>
                    <Text
                      role="heading"
                      aria-level={3}
                      style={[styles.townHeading, { color: theme.text, fontFamily: Fonts?.serif }]}>
                      {t('hotels.sectionHeading', { inCity })}
                    </Text>
                    <Text style={[styles.townText, { color: theme.textSecondary }]}>
                      {copy.cities[city.id]?.text}
                    </Text>
                    <View style={styles.townActions}>
                      <Button
                        variant="outline"
                        size="sm"
                        leadingIcon={<BedDouble size={16} color={theme.text} />}
                        onPress={() => openCityHotels(city.id, locale)}>
                        {t('hotels.ctaCity', { inCity })}
                      </Button>
                      {/* Native has no map to show. */}
                      {Platform.OS === 'web' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          leadingIcon={<MapIcon size={16} color={theme.text} />}
                          onPress={() => showOnMap(city.id)}>
                          {t('hotelsPage.showOnMap')}
                        </Button>
                      ) : null}
                      <MoreLink
                        label={t(`cityPages.${city.id}.title`)}
                        href={{ pathname: '/[lang]/[city]', params: { lang: locale, city: city.id } }}
                      />
                    </View>
                  </View>
                );
              })}
            </View>

            <Faq heading={copy.faqHeading} entries={copy.faq} />
          </View>
        </View>
        <SiteFooter />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Spacing.two,
    paddingVertical: 6,
    borderWidth: 1,
    borderRadius: 999,
  },
  chipText: {
    fontSize: 13,
    fontWeight: '500',
  },
  areaCta: {
    alignSelf: 'flex-start',
  },
  town: {
    borderWidth: 1,
    borderRadius: Radius.lg,
    padding: Spacing.three,
    gap: Spacing.two,
  },
  townHeading: {
    fontSize: 18,
    fontWeight: '700',
  },
  townText: {
    fontSize: 15,
    lineHeight: 23,
  },
  townActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: Spacing.three,
    rowGap: Spacing.two,
  },
});
